import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL, Helmet } from '@plone/volto/helpers';
import { resolveImageURL } from 'volto-essvoltocomponents/helpers';
import PageHeaderBackground from '../../Molecules/PageHeaderBackground';
import FilterMenu from '../../Molecules/FilterMenu';
import TeaserCardVertical from '../../Molecules/TeaserCard/TeaserCardVertical';
import ResponsiveGrid from '../../Atoms/ResponsiveGrid';
import Carousel from '../../Atoms/Carousel';
import ResponsiveComponentSwitch, {
  useWindowWidth,
} from '../../Atoms/ResponsiveComponentSwitch';
import Pagination from '../../Molecules/Pagination/Pagination';
import {
  ContentWrapper,
  ContentWide,
  PageSection,
} from 'volto-essvoltocomponents/components/Layout';
import Dropdown from '../../Molecules/DropDown/Dropdown';
import { stripHTML } from 'volto-essvoltocomponents/helpers';
import {
  COLLABORATORS_GRID_BREAKPOINTS,
  COLLABORATORS_ITEMS_PER_PAGE,
  COLLABORATORS_METADATA_FIELDS,
  DEFAULT_GRID_BREAKPOINTS,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_METADATA_FIELDS,
  EMPTY_ITEMS,
  FILTER_ALL,
  FILTER_CATEGORIES,
  FILTER_OTHERS,
  MOBILE_ITEMS_PER_PAGE,
  PROJECT_SECTORS_SUBREQUEST,
  PROJECT_SUB_FILTERS,
  SECTION_TO_FILTER,
} from './constants';
import {
  buildSearchQuery,
  filterProjects,
  getCardCaption,
  getCardLabel,
  getRichTextHTML,
  hasSectorTitles,
  sortProjects,
} from './helpers';
import './OurWorkView.scss';

const messages = defineMessages({
  filterAll: {
    id: 'our_work.filter_all',
    defaultMessage: 'View all',
  },
  filterOthers: {
    id: 'our_work.filter_others',
    defaultMessage: 'Other sectors',
  },
  filterBy: {
    id: 'our_work.filter_by',
    defaultMessage: 'Filter by:',
  },
  statusFilter: {
    id: 'our_work.filter_status',
    defaultMessage: 'Status',
  },
  sectorFilter: {
    id: 'our_work.filter_sector',
    defaultMessage: 'Sector',
  },
  listingRange: {
    id: 'our_work.listing_range',
    defaultMessage: '{start}–{end} of {total}',
  },
});

/**
 * OurWorkView – Default view for the our_work content type.
 *
 * Displays teaser contents classified into sectors, services, projects,
 * competences and collaborators, each accessible through a responsive filter
 * menu in the page header.
 *
 * Architecture:
 * - Projects are loaded once and sorted in the client: no end date first,
 *   then end date descending, then start date descending. Status and sector
 *   filters apply before that sort. Pagination of that list is client-side.
 * - Other sections stay server-paginated (b_size / b_start).
 * - From 768px: grid plus numbered pagination.
 * - Below 768px: carousel of 6 cards. Arrows move inside that block and, at
 *   the edges, load the next or previous block. A range label replaces
 *   numbered pagination. Collaborators stay on the grid at every width.
 */
const OurWorkView = ({ content }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [projectSubFilter, setProjectSubFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const windowWidth = useWindowWidth();
  const titleBgImageUrl = resolveImageURL(content, '/@@images/hero_image');

  const description =
    stripHTML(getRichTextHTML(content?.short_description)) ||
    content?.description ||
    '';

  const contentPath = content?.['@id'] ? flattenToAppURL(content['@id']) : '';

  const getInitialFilter = (search) => {
    const params = new URLSearchParams(search);
    const section = params.get('section') || 'sectors';
    return SECTION_TO_FILTER[section] || 'sector';
  };

  const activeFilter = getInitialFilter(location.search);
  const sectorParam =
    activeFilter === 'project'
      ? new URLSearchParams(location.search).get('sector') || FILTER_ALL
      : FILTER_ALL;

  // Main nav maps /our-work/projects → /our-work?section=projects without
  // remounting this view. Reset listing page when the section query changes.
  const [appliedFilter, setAppliedFilter] = useState(activeFilter);
  const sectionChanged = activeFilter !== appliedFilter;
  if (sectionChanged) {
    setAppliedFilter(activeFilter);
    setCurrentPage(1);
    setProjectSubFilter('all');
    setCarouselStartIndex(0);
  }
  const listingPage = sectionChanged ? 1 : currentPage;
  const listingSubFilter = sectionChanged ? 'all' : projectSubFilter;
  const isCarouselLayout = activeFilter !== 'partner' && windowWidth < 768;

  const itemsPerPage =
    activeFilter === 'partner'
      ? COLLABORATORS_ITEMS_PER_PAGE
      : isCarouselLayout
        ? MOBILE_ITEMS_PER_PAGE
        : DEFAULT_ITEMS_PER_PAGE;
  const metadataFields =
    activeFilter === 'partner'
      ? COLLABORATORS_METADATA_FIELDS
      : DEFAULT_METADATA_FIELDS;

  // Cache key per query (Publications pattern: include path + all filter dims)
  const subrequestKey = `ourWorkListing-${contentPath}-${activeFilter}-${projectSubFilter}-${sectorParam}-${currentPage}-${itemsPerPage}`;
  const sectorsSubrequestKey = `${PROJECT_SECTORS_SUBREQUEST}-${contentPath}`;

  // Read results + total from Redux subrequest
  const items = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.items || EMPTY_ITEMS,
  );
  const total = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.total || 0,
  );
  const loading = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.loading || false,
  );
  const loaded = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.loaded || false,
  );

  const allProjectItems = useSelector(
    (state) =>
      state.search.subrequests?.[sectorsSubrequestKey]?.items || EMPTY_ITEMS,
  );
  const allProjectItemsLoaded = useSelector(
    (state) =>
      state.search.subrequests?.[sectorsSubrequestKey]?.loaded || false,
  );
  const allProjectItemsLoading = useSelector(
    (state) =>
      state.search.subrequests?.[sectorsSubrequestKey]?.loading || false,
  );

  // One catalog fetch for projects: sector options, status filter, and sort.
  // Card fields are included so client-side pages can render teasers.
  useEffect(() => {
    if (activeFilter !== 'project' || !contentPath || allProjectItemsLoaded) {
      return;
    }
    dispatch(
      searchContent(
        contentPath,
        {
          portal_type: ['project'],
          path: contentPath,
          review_state: 'published',
          metadata_fields: DEFAULT_METADATA_FIELDS,
          b_size: 10000,
        },
        sectorsSubrequestKey,
      ),
    );
  }, [
    activeFilter,
    allProjectItemsLoaded,
    contentPath,
    dispatch,
    sectorsSubrequestKey,
  ]);

  // Other sections stay server-paginated. Projects are filtered and sorted locally.
  useEffect(() => {
    if (!contentPath || loaded || activeFilter === 'project') return;

    const query = buildSearchQuery(
      contentPath,
      activeFilter,
      currentPage,
      itemsPerPage,
      metadataFields,
    );
    dispatch(searchContent(contentPath, query, subrequestKey));
  }, [
    dispatch,
    contentPath,
    activeFilter,
    currentPage,
    itemsPerPage,
    subrequestKey,
    loaded,
    metadataFields,
  ]);

  const sectorFilterItems = useMemo(() => {
    const sectorSet = new Set();
    allProjectItems.forEach((item) => {
      const titles = item.related_sectors_titles;
      if (Array.isArray(titles)) {
        titles.forEach((title) => sectorSet.add(title));
      } else if (typeof titles === 'string' && titles) {
        sectorSet.add(titles);
      }
    });

    const sectors = [...sectorSet]
      .sort((a, b) => a.localeCompare(b))
      .map((title) => ({ id: title, label: title }));

    const hasItemsWithoutSector = allProjectItems.some(
      (item) => !hasSectorTitles(item.related_sectors_titles),
    );

    return [
      { id: FILTER_ALL, label: intl.formatMessage(messages.filterAll) },
      ...sectors,
      ...(hasItemsWithoutSector
        ? [
            {
              id: FILTER_OTHERS,
              label: intl.formatMessage(messages.filterOthers),
            },
          ]
        : []),
    ];
  }, [allProjectItems, intl]);

  const filteredProjects = useMemo(() => {
    if (activeFilter !== 'project') return EMPTY_ITEMS;
    return sortProjects(
      filterProjects(allProjectItems, listingSubFilter, sectorParam),
    );
  }, [activeFilter, allProjectItems, listingSubFilter, sectorParam]);

  const displayItems = useMemo(() => {
    if (activeFilter !== 'project') return items;
    const start = (listingPage - 1) * itemsPerPage;
    return filteredProjects.slice(start, start + itemsPerPage);
  }, [activeFilter, items, filteredProjects, listingPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    const count = activeFilter === 'project' ? filteredProjects.length : total;
    return Math.max(1, Math.ceil(count / itemsPerPage));
  }, [activeFilter, filteredProjects.length, itemsPerPage, total]);

  const isLoading =
    activeFilter === 'project'
      ? allProjectItemsLoading || !allProjectItemsLoaded
      : loading;

  // Handlers
  const handleFilterChange = (filterId) => {
    setCurrentPage(1);
    setProjectSubFilter('all');
    setCarouselStartIndex(0);

    const urlKey =
      Object.keys(SECTION_TO_FILTER).find(
        (key) => SECTION_TO_FILTER[key] === filterId,
      ) || 'sectors';

    const params = new URLSearchParams(location.search);
    params.set('section', urlKey);
    params.delete('sector');

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const handleSubFilterChange = (valueOrEvent) => {
    const value =
      typeof valueOrEvent === 'string'
        ? valueOrEvent
        : valueOrEvent?.target?.value;
    if (!value) return;
    setProjectSubFilter(value);
    setCurrentPage(1);
    setCarouselStartIndex(0);
  };

  const handleSectorFilterChange = (value) => {
    const params = new URLSearchParams(location.search);
    if (value === FILTER_ALL) {
      params.delete('sector');
    } else {
      params.set('sector', value);
    }
    setCurrentPage(1);
    setCarouselStartIndex(0);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const rangeTotal =
    activeFilter === 'project' ? filteredProjects.length : total;
  const rangeStart = displayItems.length
    ? (listingPage - 1) * itemsPerPage + 1
    : 0;
  const rangeEnd = rangeStart + displayItems.length - 1;

  const showNextCarouselPage = () => {
    setCarouselStartIndex(0);
    setCurrentPage((page) => page + 1);
  };

  const showPrevCarouselPage = () => {
    setCarouselStartIndex(MOBILE_ITEMS_PER_PAGE - 1);
    setCurrentPage((page) => page - 1);
  };

  // Card renderer
  const defaultCardElements = displayItems.map((item, index) => {
    const imageUrl = resolveImageURL(item, '/@@images/thumbnail_image/preview');
    const label = getCardLabel(item);
    const caption = getCardCaption(item);
    const tags = item.Subject || item.subjects || [];

    return (
      <TeaserCardVertical
        key={item['@id'] || index}
        title={item.title || ''}
        description={
          item.short_description?.data || item.description?.data || ''
        }
        imageUrl={imageUrl}
        url={item['@id'] || ''}
        label={label}
        caption={caption}
        tags={tags}
        className={item.portal_type + '-item'}
      />
    );
  });

  const collaboratorCardElements = displayItems.map((item, index) => {
    const imageUrl = resolveImageURL(item, '/@@images/thumbnail_image/preview');

    return (
      <TeaserCardVertical
        key={item['@id'] || index}
        imageUrl={imageUrl}
        url={item['url'] || ''}
        fitImage={true}
        showCTA={false}
        border="solid 1px var(--color-neutral-01)"
      />
    );
  });

  const defaultCardContainer = (
    <ResponsiveComponentSwitch
      breakpoints={[
        {
          minWidth: 768,
          component: (
            <>
              <ResponsiveGrid
                breakpoints={DEFAULT_GRID_BREAKPOINTS}
                gap="1.5rem"
              >
                {defaultCardElements}
              </ResponsiveGrid>
              {!isLoading && totalPages > 1 && (
                <div className="our-work-view__pagination">
                  <Pagination
                    currentPage={listingPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          ),
        },
      ]}
      defaultComponent={
        <>
          <Carousel
            key={`${listingPage}-${carouselStartIndex}`}
            pageKey={`${listingPage}-${carouselStartIndex}`}
            startIndex={carouselStartIndex}
            onEdgeNext={
              listingPage < totalPages ? showNextCarouselPage : undefined
            }
            onEdgePrev={listingPage > 1 ? showPrevCarouselPage : undefined}
            range={{
              start: rangeStart,
              end: rangeEnd,
              total: rangeTotal,
            }}
          >
            {defaultCardElements}
          </Carousel>
        </>
      }
    />
  );

  const collaboratorsCardContainer = (
    <>
      <ResponsiveGrid breakpoints={COLLABORATORS_GRID_BREAKPOINTS} gap="1rem">
        {collaboratorCardElements}
      </ResponsiveGrid>
      {!isLoading && totalPages > 1 && (
        <div className="our-work-view__pagination">
          <Pagination
            currentPage={listingPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </>
  );

  const cardContainer =
    activeFilter === 'partner'
      ? collaboratorsCardContainer
      : defaultCardContainer;

  return (
    <div className="our-work-view">
      <Helmet title={content?.title} />

      {/* Hero + filter menu */}
      <PageHeaderBackground
        title={content?.title || ''}
        description={description}
        backgroundImage={titleBgImageUrl}
        grayscale={100}
        imageOpacity={0.4}
        overlayOpacity={0.3}
        alignment="center"
        mobileAlignment="center"
      >
        <FilterMenu
          items={FILTER_CATEGORIES}
          activeId={activeFilter}
          onChange={handleFilterChange}
          mobileLabel="Select a category to explore:"
          className="our-work-view__filter-menu"
          centered
        />
      </PageHeaderBackground>

      {/* Content listing */}
      <PageSection
        paddingBottom="var(--ess-space-7xl)"
        className="our-work-view__listing-section"
      >
        <ContentWrapper
          className="our-work-view__listing-section-content"
          minHeight="51vh"
        >
          <ContentWide alignment="center">
            {/* Section header: title + optional project filters */}
            <div className="our-work-view__section-header">
              <h2 className="our-work-view__section-title">
                {FILTER_CATEGORIES.find((c) => c.id === activeFilter)?.label ||
                  'Results'}
              </h2>

              {activeFilter === 'project' && (
                <div className="our-work-view__sub-filters">
                  <span className="our-work-view__sub-filters__label">
                    {intl.formatMessage(messages.filterBy)}
                  </span>
                  <Dropdown
                    className="our-work-view__sub-filters__dropdown"
                    label={
                      PROJECT_SUB_FILTERS.find((f) => f.id === projectSubFilter)
                        ?.label || intl.formatMessage(messages.statusFilter)
                    }
                    triggerSize="sm"
                    menuItemSize="sm"
                    items={PROJECT_SUB_FILTERS.map((item) => ({
                      id: item.id,
                      label: item.label,
                      onSelect: () => handleSubFilterChange(item.id),
                    }))}
                  />
                  <Dropdown
                    className="our-work-view__sub-filters__dropdown"
                    label={
                      sectorFilterItems.find((item) => item.id === sectorParam)
                        ?.label || intl.formatMessage(messages.sectorFilter)
                    }
                    triggerSize="sm"
                    menuItemSize="sm"
                    items={sectorFilterItems.map((item) => ({
                      id: item.id,
                      label: item.label,
                      onSelect: () => handleSectorFilterChange(item.id),
                    }))}
                  />
                </div>
              )}
            </div>

            {/* Loading */}
            {isLoading && <p className="our-work-view__loading">Loading…</p>}

            {/* Empty */}
            {!isLoading && displayItems.length === 0 && (
              <p className="our-work-view__empty">
                No{' '}
                {FILTER_CATEGORIES.find(
                  (c) => c.id === activeFilter,
                )?.label?.toLowerCase() || 'items'}{' '}
                found.
              </p>
            )}

            {/* Responsive grid: carousel on mobile, grid on desktop */}
            {!isLoading && displayItems.length > 0 && cardContainer}
          </ContentWide>
        </ContentWrapper>
      </PageSection>
    </div>
  );
};

export default OurWorkView;
